import { Modal, Input, Button, Select, DatePicker, message, Grid } from "antd";
import { PhoneOutlined, MessageOutlined, FileTextOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  getDealActivitiesApi,
  addDealActivityApi,
} from "../../../../apiservice/crm";
import { incrementActivityCount } from "../dealSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import { convertToCompanyTimezoneObject, formatDayjsToUtc } from "../../../../lib/functions";
import "../../crmSharedStyle.css";

const DealActivityModal = ({ open, onClose, deal }) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [text, setText] = useState("");
  const [list, setList] = useState([]);
  const [type, setType] = useState("note");
  const [callStatus, setCallStatus] = useState(null);
  const [duration, setDuration] = useState(null);
  const [followUpDate, setFollowUpDate] = useState(null);

  const scrollRef = useRef(null);

  const dispatch = useDispatch();

  const authInfo = useSelector(selectAuthInfo);

  const load = async () => {
    if (!deal?.deal_id) return;

    const res = await getDealActivitiesApi({
      deal_id: deal.deal_id,
    });

    setList(res.data || []);
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [list]);

  const save = async () => {
    if (!text.trim()) return;

    // frontend validation
    if (type === "call" && !callStatus) {
      return message.warning("Call status required");
    }

    await addDealActivityApi({
      deal_id: deal.deal_id,
      text,
      type,
      call_status: callStatus,
      duration,
      followUpDate: followUpDate
        // ? followUpDate.format("YYYY-MM-DD HH:mm:ss")
        ? formatDayjsToUtc(followUpDate)
        : null,
    });

    dispatch(incrementActivityCount(deal.deal_id));

    // reset
    setText("");
    setCallStatus(null);
    setDuration(null);
    setFollowUpDate(null);
    setType("note");

    load();
  };

  const getIcon = (type) => {
    if (type === "call") return <PhoneOutlined />;
    if (type === "whatsapp") return <MessageOutlined />;
    return <FileTextOutlined />;
  };

  const getBg = (type) => {
    if (type === "call") return "#e6f4ff";
    if (type === "whatsapp") return "#f6ffed";
    return "#f5f5f5";
  };
  

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Activity — ${deal?.title}`}
      footer={null}
      width={isMobile ? "95%" : 600}
      className="crm-modal-responsive"
    >
      <div style={{ maxHeight: isMobile ? 320 : 400, overflowY: "auto", marginBottom: 12 }}>
        {list.length ? (
          list.map((item) => {
            const isMe = item.created_by === authInfo?.userInfo?.user_id;

            return (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  justifyContent: isMe ? "flex-end" : "flex-start",
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    background: getBg(item.type),
                    padding: 12,
                    borderRadius: 12,
                    maxWidth: "70%",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  }}
                >
                  {/* Header */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {getIcon(item.type)}
                    <b style={{ textTransform: "capitalize" }}>
                      {item.type || "note"}
                    </b>
                  </div>

                  {/* Content */}
                  <div style={{ marginTop: 6 }}>{item.text}</div>

                  {/* Call details */}
                  {item.type === "call" && (
                    <div style={{ marginTop: 6, fontSize: 12 }}>
                      Status: {item.call_status || "-"} <br />
                      Duration: {item.duration ? `${item.duration}s` : "-"}
                    </div>
                  )}

                  {/* Time */}
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      color: "#888",
                      textAlign: "right",
                    }}
                  >
                    {
                    // dayjs(item.created_at).format("DD MMM, hh:mm A")
                    convertToCompanyTimezoneObject(item.created_at).format("DD MMM, hh:mm A")                    
                    }
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <p>No activity yet</p>
        )}
        <div ref={scrollRef} />
      </div>

      <div className="crm-section-title" style={{ marginBottom: 8 }}>
        Add update
      </div>

      <Select
        value={type}
        onChange={setType}
        style={{ width: "100%", marginBottom: 10 }}
        options={[
          { label: "Note", value: "note" },
          { label: "Call", value: "call" },
          { label: "WhatsApp", value: "whatsapp" },
        ]}
      />

      {type === "call" && (
        <>
          <Select
            placeholder="Call Status"
            value={callStatus}
            onChange={setCallStatus}
            style={{ width: "100%", marginBottom: 10 }}
            options={[
              { label: "Connected", value: "connected" },
              { label: "Missed", value: "missed" },
              { label: "No Answer", value: "no_answer" },
            ]}
          />

          <Input
            placeholder="Duration (seconds)"
            type="number"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            style={{ marginBottom: 10 }}
          />
        </>
      )}

      <DatePicker
        showTime
        placeholder="Follow-up date (optional)"
        style={{ width: "100%", marginBottom: 10 }}
        value={followUpDate}
        onChange={(val) => setFollowUpDate(val)}
      />

      <Input.TextArea
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type activity..."
      />

      <Button type="primary" onClick={save} block style={{ marginTop: 10 }}>
        Send
      </Button>
    </Modal>
  );
};

export default DealActivityModal;